import React, {useState, useEffect} from 'react'
import GenericTable from '../GenericTable/table'
import {enquiry, fetchEnquiryData} from './helper'
import SkeletonCard from '../Auth/Components/SkeletonCard'
import Swal from 'sweetalert2'
import { Tooltip } from 'react-tooltip'
import {updateAddOnEnquiry} from '../../../api'

type SortConfig = {key: string; direction: 'asc' | 'desc' | null}
const AddOnTable = () => {
  const [columns, setColumns] = useState<string[]>([])
  const [data, setData] = useState<Record<string, any>[]>([])
  const [searchTerm, setSearchTerm] = useState<string>('')
  const [currentPage, setCurrentPage] = useState<number>(1)
  const rowsPerPage = 10
  const [totalPages, setTotalPages] = useState<number>(0)
  const [loading, setLoading] = useState<boolean>(true)
  const [currentPageData, setCurrentPageData] = useState<Record<string, any>[]>([])
  const [enquiryCount, setEnquiryCount] = useState<number>(0)
  const [sortConfig, setSortConfig] = useState<SortConfig>({key: '', direction: null})

  useEffect(() => {
    setColumns(enquiry.columns)
    fetchData()
  }, [])

  useEffect(() => {
    setTotalPages(Math.ceil(data.length / rowsPerPage))
    const startIndex = (currentPage - 1) * rowsPerPage
    const endIndex = startIndex + rowsPerPage
    setCurrentPageData(data.slice(startIndex, endIndex))
  }, [data, currentPage])

  const fetchData = async () => {
    try {
      setLoading(true)
      const mappedData = await fetchEnquiryData()
      setData(mappedData)
      setLoading(false)
    } catch (error) {
      console.error('Failed to fetch data:', error)
      alert('There was an error fetching the data.')
    }
  }

  const sortData = (data: Record<string, any>[], column: string) => {
    const sortableData = [...data]
    sortableData.sort((a, b) => {
      const aValue = a[column]
      const bValue = b[column]
      if (column === 'Farmer ID') {
        const aNumber = parseFloat(aValue)
        const bNumber = parseFloat(bValue)
        if (!isNaN(aNumber) && !isNaN(bNumber)) {
          return sortConfig.direction === 'asc' ? aNumber - bNumber : bNumber - aNumber
        }
      }

      const aDate = Date.parse(aValue)
      const bDate = Date.parse(bValue)
      if (!isNaN(aDate) && !isNaN(bDate)) {
        return sortConfig.direction === 'asc' ? aDate - bDate : bDate - aDate
      }
      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return sortConfig.direction === 'asc' ? aValue - bValue : bValue - aValue
      }

      if (typeof aValue === 'string' && typeof bValue === 'string') {
        return sortConfig.direction === 'asc'
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue)
      }

      return 0
    })

    return sortableData
  }

  const handleSort = (column: string) => {
    setSortConfig((prevConfig) => {
      const direction = prevConfig.key === column && prevConfig.direction === 'asc' ? 'desc' : 'asc';
      return { key: column, direction };
    });
  };
  
  useEffect(() => {
    if (sortConfig.key) {
      const sortedData = sortData(data, sortConfig.key);
      setData(sortedData);
    }
  }, [sortConfig]);

  const handleNextClick = () => {
    if (currentPage < totalPages) {
      setCurrentPage((prevPage) => prevPage + 1)
    }
  }

  const handlePreviousClick = () => {
    if (currentPage > 1) {
      setCurrentPage((prevPage) => prevPage - 1)
    }
  }

  const handleStatusChange = (index: number, newStatus: string) => {
    setData((prevData) => {
      const farmerName = prevData[index]?.['Farmer Name'] // Get farmer's name
      const enqueryId = prevData[index]?.item_no // Get enquery_id for API request

      // Show confirmation dialog
      Swal.fire({
        title: `Are you sure you want to update the status of this user ${farmerName}?`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Yes, update it!',
        cancelButtonText: 'Cancel',
      }).then(async (result) => {
        if (result.isConfirmed) {
          try {
            // Send API request to update status in the database
            await updateAddOnEnquiry({
              updates: [{enquery_id: enqueryId, status: newStatus}],
            })

            // If API is successful, update local state
            setData((prevState) => {
              const updatedData = [...prevState]
              updatedData[index] = {...updatedData[index], status: newStatus}
              return updatedData
            })

            Swal.fire('Updated!', `The status has been updated to ${newStatus}.`, 'success')
          } catch (error) {
            Swal.fire('Error!', 'Failed to update status. Please try again.', 'error')
          }
        }
      })

      return prevData // Return original data until confirmation
    })
  }
  useEffect(() => {
    // Set the total enquiry count based on all available data
    setEnquiryCount(data.length)
  }, [data])

  return (
    <div>
      {loading ? (
        <SkeletonCard />
      ) : (
        <>
          <GenericTable
            columns={columns}
            handleNextClick={handleNextClick}
            handlePriviousClick={handlePreviousClick}
            handleSort={handleSort}
            sortConfig={sortConfig}
            data={currentPageData.filter((row) =>
              Object.values(row).join(' ').toLowerCase().includes(searchTerm.toLowerCase())
            )}
            searchTerm={searchTerm}
            onSearch={(term) => {
              setSearchTerm(term)
              setCurrentPage(1)
            }}
            renderCell={(column, row, rowIndex) => {
              if (column === 'status') {
                return (
                  <select
                    value={row.status}
                    className='form-select form-select-solid w-150px'
                    onChange={(e) => handleStatusChange(rowIndex, e.target.value)}
                  >
                    <option value='pending'>Pending</option>
                    <option value='completed'>Completed</option>
                  </select>
                )
              }
              if (column === 'Feature' || column === 'Message') {
                const text = row[column] || "";
                const maxLength = 20;
                const truncatedText = text.length > maxLength ? text.slice(0, maxLength) + "..." : text;
              
                return (
                  <>
                    <span data-tooltip-id="comment-tooltip" data-tooltip-content={text}>
                      {truncatedText}
                    </span>
                    <Tooltip id="comment-tooltip" 
                        place="bottom" // Tooltip position: top, bottom, left, right
                        style={{ backgroundColor: "#6D9300", color: "#fff", padding: "4px", borderRadius: "6px" }}/>
                  </>
                );
              }
              return row[column]
            }}
            // conditionalClass="card card-flush mb-6 mb-xl-9 mt-5"
            LeftSideElement={
              <span
                className='badge badge-light-success'
                style={{
                  fontSize: '14px',
                  fontWeight: 'bold',
                  padding: '15px',
                }}
              >
                Total Enquiries: {enquiryCount}
              </span>
            }
          />
          {/* <Card
       type="Addon" 
        onEnquiryCountUpdate={(count) => setEnquiryCount(count)}
      /> */}
        </>
      )}

      <ul className='pagination justify-content-center' style={{margin: '15px 0'}}>
        <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
          <button
            className='page-link'
            onClick={handlePreviousClick}
            disabled={currentPage === 1}
            aria-label='Previous'
          >
            &laquo; Previous
          </button>
        </li>

        <li className='page-item active'>
          <div className='d-flex align-items-center'>
            <button
              className='page-link'
              style={{
                backgroundColor: '#6D9300',
                color: '#fff',
                pointerEvents: 'none',
              }}
            >
              {currentPage}
            </button>
            <span className='mx-2'>of</span>
            <button
              className='page-link'
              style={{
                backgroundColor: '#6D9300',
                color: '#fff',
                pointerEvents: 'none',
              }}
            >
              {totalPages}
            </button>
          </div>
        </li>

        <li className={`page-item ${currentPage >= totalPages ? 'disabled' : ''}`}>
          <button
            className='page-link'
            onClick={handleNextClick}
            disabled={currentPage >= totalPages}
            aria-label='Next'
          >
            Next &raquo;
          </button>
        </li>
      </ul>
    </div>
  )
}

export default AddOnTable
